import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface Project {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  repositories?: any[]; // Optional, populated when fetching with relations
  // Ticket Management Configuration
  ticketProvider: 'jira' | 'asana' | null;
  externalProjectId: string | null;
  statusPickup: string | null;
  statusWorking: string | null;
  statusReview: string | null;
  statusDone: string | null;
  statusClosed: string | null;
}

interface ProjectsState {
  projects: Project[];
  currentProject: Project | null;
  agentWork: any[] | null;
  agentWorkStatus: 'idle' | 'loading' | 'success' | 'failed';
  chatSessionHistory: any[] | null;
  chatSessionHistoryStatus: 'idle' | 'loading' | 'success' | 'failed';
  insightReportHistory: any[] | null;
  insightReportHistoryStatus: 'idle' | 'loading' | 'success' | 'failed';
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: ProjectsState = {
  projects: [],
  currentProject: null,
  agentWork: null,
  agentWorkStatus: 'idle',
  chatSessionHistory: null,
  chatSessionHistoryStatus: 'idle',
  insightReportHistory: null,
  insightReportHistoryStatus: 'idle',
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getProjects = createAsyncThunk(
  'projects/getProjects',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      // Use generic error message for security - don't expose internal details
      console.error('Fetch projects error:', error);
      return rejectWithValue('Something went wrong while loading projects. Please try again.');
    }
  }
);

export const getProject = createAsyncThunk(
  'projects/getProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/${projectId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData: { organizationId: string; name: string; description?: string }, { rejectWithValue }) => {
    try {
      // Remove organizationId from payload - backend adds it from authenticated user
      const { organizationId, ...dataToSend } = projectData;
      const response = await apiRequest('/projects', {
        method: 'POST',
        body: JSON.stringify(dataToSend),
      });
      return response;
    } catch (error: any) {
      // Pass full error object for plan limit detection
      // API returns: { error: { code: 'PLAN_LIMIT_EXCEEDED', message: '...' } }
      if (error?.error?.code === 'PLAN_LIMIT_EXCEEDED') {
        return rejectWithValue(error);
      }
      // Use generic error message for security - don't expose internal details
      console.error('Create project error:', error);
      return rejectWithValue('Something went wrong while creating the project. Please try again.');
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, data }: { id: string; data: { name?: string; description?: string } }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error: any) {
      console.error('Update project error:', error);
      return rejectWithValue('Something went wrong while updating the project. Please try again.');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/projects/${projectId}`, {
        method: 'DELETE',
      });
      return projectId;
    } catch (error: any) {
      console.error('Delete project error:', error);
      return rejectWithValue('Something went wrong while deleting the project. Please try again.');
    }
  }
);

export const toggleProjectActive = createAsyncThunk(
  'projects/toggleProjectActive',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/${projectId}/toggle-active`, {
        method: 'PATCH',
      });
      return response;
    } catch (error: any) {
      console.error('Toggle project error:', error);
      return rejectWithValue('Something went wrong while toggling the project. Please try again.');
    }
  }
);

export const getRecentActivity = createAsyncThunk(
  'projects/getRecentActivity',
  async ({ organizationId, limit = 6 }: { organizationId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/recent-activity?organizationId=${organizationId}&limit=${limit}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAgentWork = createAsyncThunk(
  'projects/getAgentWork',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/${projectId}/agent-work`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getChatSessionHistory = createAsyncThunk(
  'projects/getChatSessionHistory',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/${projectId}/chat-session-history`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getInsightReportHistory = createAsyncThunk(
  'projects/getInsightReportHistory',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/projects/${projectId}/insight-report-history`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get projects
  [getProjects.pending.type]: { status: 'loading' as const },
  [getProjects.fulfilled.type]: (state: ProjectsState, action: any) => {
    // Handle null response (404 - no projects found)
    if (!action.payload) {
      state.projects = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    const projects = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.projects || action.payload.projects || [];

    state.projects = projects;
    state.status = 'success';
    state.error = '';
  },
  [getProjects.rejected.type]: (state: ProjectsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch projects';
  },

  // Get single project
  [getProject.pending.type]: { status: 'loading' as const },
  [getProject.fulfilled.type]: (state: ProjectsState, action: any) => {
    // Handle null response (404 - project not found)
    if (!action.payload) {
      state.currentProject = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: {...}, metadata: {...} }
    state.currentProject = action.payload.response || action.payload;
    state.status = 'success';
    state.error = '';
  },
  [getProject.rejected.type]: (state: ProjectsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch project';
  },

  // Create project - don't update status to avoid re-renders since we navigate immediately
  [createProject.pending.type]: (state: ProjectsState) => {
    state.error = '';
  },
  [createProject.fulfilled.type]: (state: ProjectsState) => {
    // Don't update status or projects list - we navigate to the new project immediately
    state.error = '';
  },
  [createProject.rejected.type]: (state: ProjectsState, action: any) => {
    state.status = 'failed';
    // Handle plan limit error - don't set error message as it will be handled by dialog
    if (action.payload?.error?.code === 'PLAN_LIMIT_EXCEEDED') {
      state.error = '';
      return;
    }
    state.error = typeof action.payload === 'string' ? action.payload : action.payload?.message || 'Failed to create project';
  },

  // Update project
  [updateProject.pending.type]: { status: 'loading' as const },
  [updateProject.fulfilled.type]: (state: ProjectsState, action: any) => {
    const updatedProject = action.payload.response || action.payload;
    const index = state.projects.findIndex((p) => p.id === updatedProject.id);
    if (index !== -1) {
      state.projects[index] = updatedProject;
    }
    if (state.currentProject?.id === updatedProject.id) {
      state.currentProject = updatedProject;
    }
    state.status = 'success';
    state.error = '';
  },
  [updateProject.rejected.type]: (state: ProjectsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to update project';
  },

  // Delete project
  [deleteProject.pending.type]: { status: 'loading' as const },
  [deleteProject.fulfilled.type]: (state: ProjectsState, action: any) => {
    const projectId = action.payload;
    state.projects = state.projects.filter((p) => p.id !== projectId);
    if (state.currentProject?.id === projectId) {
      state.currentProject = null;
    }
    state.status = 'success';
    state.error = '';
  },
  [deleteProject.rejected.type]: (state: ProjectsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to delete project';
  },

  // Toggle active
  [toggleProjectActive.pending.type]: { status: 'loading' as const },
  [toggleProjectActive.fulfilled.type]: (state: ProjectsState, action: any) => {
    const updatedProject = action.payload.response || action.payload;
    const index = state.projects.findIndex((p) => p.id === updatedProject.id);
    if (index !== -1) {
      state.projects[index] = updatedProject;
    }
    if (state.currentProject?.id === updatedProject.id) {
      state.currentProject = updatedProject;
    }
    state.status = 'success';
    state.error = '';
  },
  [toggleProjectActive.rejected.type]: (state: ProjectsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to toggle project active status';
  },

  // Get agent work
  [getAgentWork.pending.type]: { agentWorkStatus: 'loading' as const },
  [getAgentWork.fulfilled.type]: (state: ProjectsState, action: any) => {
    const data = action.payload.response || action.payload;
    state.agentWork = data.agentWork || [];
    state.agentWorkStatus = 'success';
    state.error = '';
  },
  [getAgentWork.rejected.type]: (state: ProjectsState, action: any) => {
    state.agentWorkStatus = 'failed';
    state.error = action.payload || 'Failed to fetch agent work';
  },

  // Get chat session history
  [getChatSessionHistory.pending.type]: (state: ProjectsState) => {
    state.chatSessionHistoryStatus = 'loading';
  },
  [getChatSessionHistory.fulfilled.type]: (state: ProjectsState, action: any) => {
    const data = action.payload.response || action.payload;
    state.chatSessionHistory = data.chatSessions || [];
    state.chatSessionHistoryStatus = 'success';
  },
  [getChatSessionHistory.rejected.type]: (state: ProjectsState, action: any) => {
    state.chatSessionHistoryStatus = 'failed';
    state.error = action.payload || 'Failed to fetch chat session history';
  },

  // Get insight report history
  [getInsightReportHistory.pending.type]: (state: ProjectsState) => {
    state.insightReportHistoryStatus = 'loading';
  },
  [getInsightReportHistory.fulfilled.type]: (state: ProjectsState, action: any) => {
    const data = action.payload.response || action.payload;
    state.insightReportHistory = data.insightReports || [];
    state.insightReportHistoryStatus = 'success';
  },
  [getInsightReportHistory.rejected.type]: (state: ProjectsState, action: any) => {
    state.insightReportHistoryStatus = 'failed';
    state.error = action.payload || 'Failed to fetch insight report history';
  },
};

// ==============================|| SLICE ||============================== //

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentProject(state, action) {
      state.currentProject = action.payload;
    },
    clearProjectsState(state) {
      state.projects = [];
      state.currentProject = null;
      state.status = 'idle';
      state.error = '';
      state.agentWork = null;
      state.agentWorkStatus = 'idle';
      state.chatSessionHistory = null;
      state.chatSessionHistoryStatus = 'idle';
      state.insightReportHistory = null;
      state.insightReportHistoryStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentProject, clearProjectsState } = projectsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectProjects = (state: any) => state.projects.projects;
export const selectCurrentProject = (state: any) => state.projects.currentProject;
export const selectProjectsStatus = (state: any) => state.projects.status;
export const selectProjectsError = (state: any) => state.projects.error;
export const selectAgentWork = (state: any) => state.projects.agentWork;
export const selectAgentWorkStatus = (state: any) => state.projects.agentWorkStatus;
export const selectChatSessionHistory = (state: any) => state.projects.chatSessionHistory;
export const selectChatSessionHistoryStatus = (state: any) => state.projects.chatSessionHistoryStatus;
export const selectInsightReportHistory = (state: any) => state.projects.insightReportHistory;
export const selectInsightReportHistoryStatus = (state: any) => state.projects.insightReportHistoryStatus;

export default projectsSlice.reducer;
